import React, { useEffect, useRef } from 'react';
import { observer, useLocalStore } from 'mobx-react';
import useMobxStores from 'hooks/useMobxStores';
import { useTranslation } from 'react-i18next';
import { LabsAlertGroupProvider } from '@ole-ui/ole-ui-components';
import LabStore from 'stores/Lab';
import { Duration } from 'luxon';
import { AlertActionLink } from '@patternfly/react-core';

import './styles.scss';

type LabsAlertsChannelMessage = {
  type: 'dismiss' | 'labExtended';
  alertKey: string | null;
  labId: string | null;
};

const LabsAlerts: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const { userStore } = useMobxStores();
  const { addAlert, hasAlert, removeAlert } =
    LabsAlertGroupProvider.useLabsAlertGroupContext();
  const nearShutdownCommand = 'dismiss_near_shutdown_alert';
  const recentlyShutdownCommand = 'dismiss_recently_shutdown_alert';

  const labStore = useLocalStore(() => new LabStore(userStore));
  useEffect(() => {
    labStore.startPolling();

    return () => labStore.stopPolling();
  }, []);

  const channelRef = useRef<BroadcastChannel | null>(null);
  useEffect(() => {
    channelRef.current = new BroadcastChannel('labs_alerts_channel');

    // Listen for messages from other tabs
    channelRef.current.onmessage = (event) => {
      const message = event.data as LabsAlertsChannelMessage;

      switch (message.type) {
        case 'dismiss':
          removeAlert(message.alertKey);
          break;
        case 'labExtended':
          // eslint-disable-next-line @typescript-eslint/no-use-before-define
          alertLabExtended(message.labId);
          break;
        default:
          // fallback for unexpected values
          console.warn('Unhandled message type:', message);
      }

      return () => {
        channelRef.current?.close();
        channelRef.current = null;
      };
    };
  }, []);

  const dismissAlert = (
    alertKey: string,
    labId: string,
    command: string | null = null,
  ): void => {
    removeAlert(alertKey);
    if (command) LabStore.dismissAlert(labId, command);
    channelRef.current?.postMessage({ type: 'dismiss', alertKey }); // Broadcast to other tabs
  };

  const handleExtendClick = (alertKey: string, labId: string): void => {
    dismissAlert(alertKey, labId, nearShutdownCommand);
    LabStore.extendAutoStop(labId);
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    alertLabExtended(labId);
    channelRef.current?.postMessage({
      type: 'labExtended',
      labId,
    });
  };

  function alertLabExtended(labId: string): void {
    const courseCode = labStore.getCourseCode(labId);
    const alertKey = `lab-extended-${labId}`;
    addAlert({
      key: alertKey,
      variant: 'info',
      className: 'labs-alert',
      title: t('Lab session extended successfully.'),
      children: t(
        'Your lab session on course {{courseCode}} has been extended by 1 hour.',
        { courseCode },
      ),
      actionLinks: (
        <AlertActionLink
          key={`dismiss-btn-${labId}`}
          variant="link"
          onClick={() => dismissAlert(alertKey, labId)}
        >
          {t('Dismiss')}
        </AlertActionLink>
      ),
      onClose: () => dismissAlert(alertKey, labId),
    });
  }

  function alertNearShutdown(labId: string): void {
    const alertKey = `near-shutdown-${labId}`;
    const remainingMs = labStore.remainingShutdownTime(labId);
    const dur = Duration.fromMillis(remainingMs).shiftTo('hours', 'minutes');
    const h = Math.floor(dur.hours);
    const m = Math.ceil(dur.minutes);
    const parts = h > 0 ? { hours: h, minutes: m } : { minutes: m };
    const remainingMinutes = Duration.fromObject(parts).toHuman({
      unitDisplay: 'long',
    });
    const courseCode = labStore.getCourseCode(labId);

    addAlert({
      key: alertKey,
      variant: 'warning',
      className: 'labs-alert',
      title: t('Lab will stop in {{remainingMinutes}}.', { remainingMinutes }),
      children: t(
        'Your lab session on course {{courseCode}} will automatically stop in {{remainingMinutes}}. To continue working use the button below to extend immediately.',
        { courseCode, remainingMinutes },
      ),

      actionLinks: (
        <>
          <AlertActionLink
            key={`extend-btn-${labId}`}
            variant="primary"
            onClick={() => handleExtendClick(alertKey, labId)}
          >
            {t('Extend session by 1 hour')}
          </AlertActionLink>
          <AlertActionLink
            key={`dismiss-btn-${labId}`}
            variant="link"
            onClick={() => dismissAlert(alertKey, labId, nearShutdownCommand)}
          >
            {t('Dismiss')}
          </AlertActionLink>
        </>
      ),
      onClose: () => dismissAlert(alertKey, labId, nearShutdownCommand),
    });
  }

  function alertRecentlyShutdown(labId: string): void {
    const alertKey = `recently-shutdown-${labId}`;
    const courseCode = labStore.getCourseCode(labId);
    addAlert({
      key: alertKey,
      variant: 'warning',
      className: 'labs-alert',
      title: t('Lab session has stopped.'),
      children: t(
        'Your lab session on course {{courseCode}} has stopped automatically. To continue working, click the START button to run the lab again. If you wish to start a lab in another offering, we recommend deleting this session first to ensure a smooth experience.',
        { courseCode },
      ),
      actionLinks: (
        <AlertActionLink
          key={`dismiss-btn-${labId}`}
          variant="link"
          onClick={() => dismissAlert(alertKey, labId, recentlyShutdownCommand)}
        >
          {t('Dismiss')}
        </AlertActionLink>
      ),
      onClose: () => dismissAlert(alertKey, labId, recentlyShutdownCommand),
    });
  }

  useEffect(() => {
    labStore.labsNearShutdown.forEach((lab) => {
      const nearShutdownAlertKey = `near-shutdown-${lab.doc_id}`;

      if (!hasAlert(nearShutdownAlertKey)) {
        alertNearShutdown(lab.doc_id);
      }
    });
  }, [labStore.labsNearShutdown]);

  useEffect(() => {
    labStore.labsRecentlyShutdown.forEach((lab) => {
      const recentlyShutdownAlertKey = `recently-shutdown-${lab.doc_id}`;
      const nearShutdownAlertKey = `near-shutdown-${lab.doc_id}`;
      const extendedLabAlertKey = `extend-button-${lab.doc_id}`;

      if (!hasAlert(recentlyShutdownAlertKey)) {
        dismissAlert(nearShutdownAlertKey, lab.doc_id, nearShutdownCommand);
        dismissAlert(extendedLabAlertKey, lab.doc_id);
        alertRecentlyShutdown(lab.doc_id);
      }
    });
  }, [labStore.labsRecentlyShutdown]);

  return null;
};

export default observer(LabsAlerts);
